import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import {
  PiPlus,
  PiCloudArrowUpLight,
  PiPaperclipLight,
  PiWarningCircleLight,
  PiXLight,
} from 'react-icons/pi';
import {
  AttachmentList,
  toAttachments,
  filterAttachments,
  sortAttachments,
  Attachment,
} from '../../shared/attachments';
import { validateFileSecure } from '../../../utils/security/fileSecurityValidator';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, taskAttachmentsTabTourSteps } from '../../shared/FeatureTour';

/**
 * The task Attachments tab.
 *
 * This used to carry its own `getFileIcon`, its own `InlineAttachmentUploader`
 * wrapper around `FileUploader`, its own `dayjs` date column and a resizable
 * `Table` that no other attachment surface matched. All of that now comes
 * from `shared/attachments`; what stays here is the task's own permission
 * rules and the endpoints the page already owned.
 *
 * THE DROP TARGET IS THE LIST, NOT A BOX ABOVE IT.
 *
 * The tab used to render a filter bar with an Upload File action AND a
 * permanent `AttachmentDropzone` AND the list — a standing drop box competing
 * with the content it exists to serve, which is what "splashed across the
 * screen" meant. The whole list area is the target now and the zone only
 * appears while something is being dragged over it; the explicit Upload File
 * button stays for everyone who does not drag. With no files at all the tab
 * collapses to ONE empty state with one call to action.
 */

/** The document whitelist and ceiling `AttachmentDropzone` defaults to. */
const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'msg', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

interface TaskAttachmentsTabContentProps {
  taskDetails: any;
  taskUuid: string;
  userAccessLevel: 'edit' | 'view' | 'owner' | 'admin';
  taskStatus: string;
  attachments: any[];
  setAttachments: React.Dispatch<React.SetStateAction<any[]>>;
  attachmentsLoading: boolean;
  handleOpenUploadModal: () => void;
  handleOpenAttachment: (attachment: any) => Promise<void>;
  handleDeleteAttachment: (attachmentId: string) => void;
  handleFileUpload?: (files: File[]) => Promise<void> | void;
}

const TaskAttachmentsTabContent: React.FC<TaskAttachmentsTabContentProps> = ({
  userAccessLevel,
  taskStatus,
  attachments,
  attachmentsLoading,
  handleOpenUploadModal,
  handleFileUpload,
  handleOpenAttachment,
  handleDeleteAttachment,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  // Nested dragenter/dragleave from the rows underneath would otherwise
  // flicker the overlay off the moment the cursor crosses a row boundary.
  const dragDepth = useRef(0);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-attachments-tab',
    steps: taskAttachmentsTabTourSteps,
    readyToShow: !attachmentsLoading,
    autoStartDelay: 600,
  });

  const canEdit =
    userAccessLevel !== 'view' && taskStatus !== 'completed' && taskStatus !== 'deleted';
  const canRemove = taskStatus === 'todo' && userAccessLevel !== 'view';
  const canDrop = canEdit && !!handleFileUpload;

  const normalized = useMemo(() => sortAttachments(toAttachments(attachments)), [attachments]);
  const visible = useMemo(() => filterAttachments(normalized, searchTerm), [normalized, searchTerm]);

  const download = (attachment: Attachment) => {
    if (!attachment.url) return;
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.click();
  };

  const endDrag = useCallback(() => {
    dragDepth.current = 0;
    setDragging(false);
  }, []);

  /* Only a file drag raises the overlay — dragging text or a link across the
     tab must not offer to upload anything. */
  const isFileDrag = (event: React.DragEvent) =>
    Array.from(event.dataTransfer?.types || []).includes('Files');

  const dragHandlers = canDrop
    ? {
        onDragEnter: (e: React.DragEvent) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        },
        onDragOver: (e: React.DragEvent) => {
          if (isFileDrag(e)) e.preventDefault();
        },
        onDragLeave: (e: React.DragEvent) => {
          if (!dragging) return;
          e.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        },
        onDrop: async (e: React.DragEvent) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          endDrag();
          const files = Array.from(e.dataTransfer.files || []);
          if (!files.length) return;
          /* The same gate `AttachmentDropzone` applies — magic bytes, not the
             browser-reported type. The standing zone used to be the only path
             in, so dropping straight onto the list must not be the way around
             it. */
          const accepted: File[] = [];
          const errors: string[] = [];
          for (const file of files) {
            const result = await validateFileSecure(file, ACCEPTED_EXTENSIONS, MAX_UPLOAD_BYTES);
            if (result.isValid) accepted.push(result.processedFile || file);
            else errors.push(`${file.name}: ${result.errors[0] || t('File rejected')}`);
          }
          setRejected(errors);
          if (accepted.length) await handleFileUpload?.(accepted);
        },
      }
    : {};

  /* Nothing on the task at all: one empty state, one button. The
     filtered-to-zero case is NOT this branch — there the controls are the way
     out, so the bar stays up and the list says "No matching files". */
  const isEmpty = !attachmentsLoading && normalized.length === 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      {!isEmpty && (
        <div data-tour-id="task-attachments-actions" className="flex-shrink-0">
          <FilterBarWithTabs
            onSearchChange={setSearchTerm}
            noParentPadding={true}
            noParentHeight={true}
            itemCount={visible.length}
            totalItemCount={normalized.length}
            itemCountLabel={t('files')}
            actions={
              canEdit
                ? [
                    {
                      label: t('Upload File'),
                      icon: <PiPlus className="h-4 w-4" />,
                      onClick: handleOpenUploadModal,
                    },
                  ]
                : []
            }
          />
        </div>
      )}

      {/* THE LIST AREA IS THE DROP TARGET. `relative` so the drag overlay can
          cover exactly it, and nothing else on the page. */}
      <div
        className="relative flex-1 min-h-0 flex flex-col"
        data-tour-id="task-attachments-table"
        {...dragHandlers}
      >
        {isEmpty ? (
          <div className="flex-1 min-h-0 flex items-center justify-center">
            <EmptyState
              density="compact"
              icon={<PiPaperclipLight />}
              headline={t('No attachments on this task yet')}
              subtext={
                canEdit
                  ? t('Drop files anywhere on this tab, or upload them from here.')
                  : t('Files added to this task appear here for everyone with access.')
              }
              button={
                canEdit
                  ? { label: t('Upload files'), onClick: handleOpenUploadModal }
                  : undefined
              }
            />
          </div>
        ) : (
          <AttachmentList
            attachments={visible}
            loading={attachmentsLoading}
            /* One row per file: name, uploaded by, date-time, size and the
               actions as aligned columns rather than a name over a metadata
               line, which read as two rows per file on a full-width tab. */
            layout="columns"
            onOpen={(attachment) => handleOpenAttachment(attachment.raw ?? attachment)}
            onDownload={download}
            onRemove={(attachment) => handleDeleteAttachment(attachment.id)}
            canRemove={canRemove}
            emptyTitle={t('No matching files')}
            emptyHint={t('Try a different search term.')}
          />
        )}

        {dragging && (
          <div
            className="absolute inset-0 z-20 rounded-lg border-2 border-dashed border-champagne bg-champagne/10 backdrop-blur-[1px] flex flex-col items-center justify-center pointer-events-none"
            data-testid="task-attachments-drop-overlay"
          >
            <PiCloudArrowUpLight className="h-8 w-8 text-champagne" />
            <p className="mt-2 text-sm text-fg-strong">{t('Drop to upload')}</p>
          </div>
        )}
      </div>

      {/* A rejected drop has to say so somewhere — the overlay is gone by the
          time the validator answers. One line under the list, dismissible. */}
      {rejected.length > 0 && (
        <div className="mt-2 flex-shrink-0 flex items-start gap-2 rounded-md border border-danger-200 dark:border-danger-800/60 bg-danger-50/60 dark:bg-danger-900/20 px-2.5 py-2">
          <PiWarningCircleLight className="h-4 w-4 text-danger-500 flex-shrink-0 mt-0.5" />
          <ul className="flex-1 min-w-0 space-y-0.5">
            {rejected.map((message) => (
              <li key={message} className="text-[11px] text-danger-700 dark:text-danger-300 break-words">
                {message}
              </li>
            ))}
          </ul>
          <button
            type="button"
            aria-label={t('Dismiss')}
            onClick={() => setRejected([])}
            className="p-1 rounded-md text-danger-500 hover:text-danger-700 transition-colors"
          >
            <PiXLight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <FeatureTour
        steps={taskAttachmentsTabTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default TaskAttachmentsTabContent;
